/* Personalized cart coordinator. Enabled by the product gate or canonical per-tab override. */
(() => {
  if (window.PopPrintsPersonalizedCheckout) return;
  const STORAGE_KEY = 'popPrints.checkoutAttempt.v1';
  const ATTEMPT = '_PP Add Attempt';
  const ROLE = '_PP Add Role';
  const CART_CONFIRMATIONS_KEY = 'popPrints.cartConfirmations.v1';
  let cartConfirmations = [];
  let confirmingCartUse = false;
  let busy = false;
  let memoryAttempt = null;
  let warm = null;
  let warmTimer;
  let pageActive = true;
  const traceTimings = new URL(location.href).searchParams.get('pp_perf') === '1';
  const trace = (stage, started, details = {}) => {
    if (traceTimings) console.info('[portrait-cart-timing]', JSON.stringify({ stage, ms: Date.now() - started, ...details }));
  };
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const numericId = (id) => String(id || '').replace(/^gid:\/\/shopify\/(Product|ProductVariant)\//, '');
  const validCartConfirmation = (entry) => entry &&
    /^[A-Za-z0-9_-]{16,100}$/.test(entry.requestId || '') &&
    /^[1-9][0-9]{5,19}$/.test(entry.productId || '') && /^[1-9][0-9]{5,19}$/.test(entry.variantId || '');
  try {
    const stored = JSON.parse(sessionStorage.getItem(CART_CONFIRMATIONS_KEY) || '[]');
    if (Array.isArray(stored)) cartConfirmations = stored.filter(validCartConfirmation).slice(-20);
  } catch (_) { /* Shopify cart webhooks independently retain the product. */ }
  const saveCartConfirmations = () => {
    try { sessionStorage.setItem(CART_CONFIRMATIONS_KEY, JSON.stringify(cartConfirmations)); } catch (_) {}
  };
  async function flushCartConfirmations() {
    if (confirmingCartUse || !cartConfirmations.length) return;
    confirmingCartUse = true;
    try {
      for (const delay of [0, 2000, 8000]) {
        if (delay) await pause(delay);
        for (const entry of [...cartConfirmations]) {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(new URL('/apps/pop-prints-backend/checkout-product/cart-confirmed', location.origin).href, {
              method: 'POST', credentials: 'same-origin', cache: 'no-store', keepalive: true,
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(entry), signal: controller.signal,
            });
            if (response.ok || [400, 401, 403, 409].includes(response.status)) {
              cartConfirmations = cartConfirmations.filter((pending) => pending.requestId !== entry.requestId);
              saveCartConfirmations();
            }
          } catch (_) { /* Keep the confirmation for this retry or the next page load. */ }
          finally { clearTimeout(timeout); }
        }
        if (!cartConfirmations.length) break;
      }
    } finally { confirmingCartUse = false; }
  }
  const confirmCartUse = (confirmation) => {
    const entry = { requestId: String(confirmation?.requestId || ''),
      productId: numericId(confirmation?.productId), variantId: numericId(confirmation?.variantId) };
    if (!validCartConfirmation(entry)) return;
    if (!cartConfirmations.some((pending) => pending.requestId === entry.requestId)) {
      cartConfirmations.push(entry);
      cartConfirmations = cartConfirmations.slice(-20);
      saveCartConfirmations();
    }
    void flushCartConfirmations();
  };
  const route = (path) => {
    const root = new URL(window.Shopify?.routes?.root || '/', location.origin);
    if (root.origin !== location.origin) throw new Error('The cart address could not be verified.');
    return new URL(root.pathname.replace(/\/?$/, '/') + path, location.origin).href;
  };
  const remember = (attempt) => {
    memoryAttempt = attempt;
    try {
      if (attempt) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* The current page still retains the attempt when storage is unavailable. */ }
  };
  const recalled = () => {
    if (memoryAttempt) return memoryAttempt;
    try {
      const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return value?.requestId && value?.fingerprint ? value : null;
    } catch (_) { return null; }
  };
  const request = async (url, options = {}) => {
    const started = Date.now();
    let status;
    const controller = new AbortController();
    const abort = () => controller.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) controller.abort();
    const timeout = setTimeout(() => controller.abort(), 30_000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      status = response.status;
      return response;
    } finally {
      trace('request', started, { path: new URL(url).pathname, status });
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    }
  };
  const cartSnapshot = async () => {
    const response = await request(route('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your cart could not be checked.');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Your cart could not be checked.');
    return cart;
  };
  const selectionFor = (options) => {
    const selection = options.selection();
    const wrapper = options.wrapper || options.form.closest?.('[data-portrait-shape]');
    const portraitShape = selection.portraitShape ?? wrapper?.dataset.portraitShape ?? options.config?.portraitShape;
    const previewThemeId = selection.previewThemeId ?? wrapper?.dataset.previewThemeId ?? options.config?.previewThemeId;
    return { ...selection, ...(portraitShape !== undefined ? { portraitShape } : {}),
      ...(previewThemeId !== undefined ? { previewThemeId } : {}) };
  };
  const nonPortrait = (selection = {}) => selection.portraitShape !== undefined && selection.portraitShape !== 'portrait';
  const fingerprint = (selection) => JSON.stringify([
    numericId(selection.sourceVariantId), String(selection.artworkUrl || ''),
    Number(selection.quantity || 1), Boolean(selection.addon), String(selection.countryCode || 'US'),
    String(selection.presentationVersion || ''),
    String(selection.portraitShape ?? 'portrait'), String(selection.previewThemeId || ''),
    ...(selection.artworkSource ? [String(selection.artworkSource)] : []),
  ]);
  const preparationKey = (selection = {}) => JSON.stringify([
    numericId(selection.sourceVariantId), String(selection.artworkUrl || ''), String(selection.countryCode || 'US'),
    String(selection.presentationVersion || ''),
    String(selection.portraitShape ?? 'portrait'), String(selection.previewThemeId || ''),
    ...(selection.artworkSource ? [String(selection.artworkSource)] : []),
  ]);
  const newAttempt = (options, selection) => {
    const design = options.design?.() || {};
    const requestId = crypto.randomUUID();
    return { requestId, fingerprint: fingerprint(selection), phase: 'preparing', body: {
      sourceVariantId: numericId(selection.sourceVariantId), artworkUrl: selection.artworkUrl,
      ...(selection.artworkSource ? { artworkSource: selection.artworkSource } : {}),
      designId: design.designId || undefined, designToken: design.designToken || undefined,
      requestId, countryCode: selection.countryCode || 'US',
      ...(selection.presentationVersion ? { presentationVersion: selection.presentationVersion } : {}),
      ...(selection.portraitShape !== undefined ? { portraitShape: selection.portraitShape } : {}),
      ...(selection.previewThemeId ? { previewThemeId: String(selection.previewThemeId) } : {}),
    } };
  };
  const assertSelection = (options, expected) => {
    if (options.isEnabled?.() === false || options.form.isConnected === false || fingerprint(selectionFor(options)) !== expected) {
      throw new Error('Your portrait or options changed. Check your selection, then add it again.');
    }
  };
  const inspectAttempt = (cart, attempt) => {
    const found = cart.items.filter((item) => item.properties?.[ATTEMPT] === attempt.requestId);
    if (!found.length) return 'absent';
    const complete = (attempt.items || []).every((expected) => found.some((item) =>
      numericId(item.variant_id || item.id) === numericId(expected.id) &&
      item.properties?.[ROLE] === expected.properties?.[ROLE] &&
      Number(item.quantity) >= Number(expected.quantity)));
    return complete && attempt.items?.length ? 'complete' : 'partial';
  };
  const statusElement = (form) => {
    let element = form.querySelector('[data-personalized-checkout-status]');
    if (!element) {
      element = document.createElement('p');
      element.dataset.personalizedCheckoutStatus = '';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      element.style.cssText = 'font-size:16px;line-height:1.5;margin:12px 0;';
      form.append(element);
    }
    return element;
  };
  // Only an explicit Add owns this overlay; background preparation stays silent.
  let cartProgress = null;
  const beginCartProgress = (form) => {
    cartProgress?.finish();
    if (!document.body || typeof document.createElement !== 'function') return null;
    if (!document.getElementById('pp-cart-progress-styles')) {
      const style = document.createElement('style');
      style.id = 'pp-cart-progress-styles';
      style.textContent = `
        .pp-cart-progress { position:fixed; inset:0; box-sizing:border-box; width:min(420px,calc(100vw - 32px));
          max-height:calc(100dvh - 32px); margin:auto; padding:38px 28px 30px; border:1px solid #e9dfd2;
          border-radius:22px; background:#fffdf9; color:#2c2420; text-align:center;
          box-shadow:0 24px 80px #211a1833; font-family:var(--font-body--family,Arial,sans-serif); }
        .pp-cart-progress::backdrop { background:#211a1880; backdrop-filter:blur(4px); }
        .pp-cart-progress:not([open]) { display:none; }
        .pp-cart-progress__hide { position:absolute; top:9px; right:9px; display:grid; place-items:center;
          width:40px; height:40px; padding:0; border:0; border-radius:50%; background:transparent;
          color:#746b61; font:26px/1 Arial,sans-serif; cursor:pointer; }
        .pp-cart-progress__hide:hover { background:#f4eee5; }
        .pp-cart-progress__hide:focus-visible { outline:2px solid #a97620; outline-offset:2px; }
        .pp-cart-progress__spinner { display:block; width:46px; height:46px; margin:0 auto 22px;
          border:3px solid #f0e4d0; border-top-color:#c8922a; border-radius:50%;
          animation:pp-cart-progress-spin 900ms linear infinite; }
        .pp-cart-progress__eyebrow { margin:0 0 9px; color:#8a652b; font-size:11px; font-weight:700;
          letter-spacing:.14em; text-transform:uppercase; }
        .pp-cart-progress h2 { margin:0; color:#2c2420; font-family:var(--font-heading--family,'Lora',Georgia,serif);
          font-size:28px; font-weight:500; line-height:1.2; text-wrap:balance; }
        .pp-cart-progress__copy { margin:14px 0 0; color:#746b61; font-size:16px; line-height:1.55; text-wrap:pretty; }
        .pp-cart-progress__note { margin:22px 0 0; padding-top:18px; border-top:1px solid #eee5d9;
          color:#746b61; font-size:13px; line-height:1.5; }
        @keyframes pp-cart-progress-spin { to { transform:rotate(360deg); } }
        @media (prefers-reduced-motion:reduce) { .pp-cart-progress__spinner { animation:none; border-top-color:#c8922a; } }
        @media (max-width:380px) { .pp-cart-progress { padding:36px 22px 26px; } .pp-cart-progress h2 { font-size:25px; } }
      `;
      document.head.append(style);
    }
    const dialog = document.createElement('dialog');
    dialog.className = 'pp-cart-progress';
    dialog.setAttribute('aria-labelledby', 'pp-cart-progress-title');
    dialog.setAttribute('aria-describedby', 'pp-cart-progress-copy');
    dialog.innerHTML = `<button class="pp-cart-progress__hide" type="button" aria-label="Hide progress message">×</button>
      <span class="pp-cart-progress__spinner" aria-hidden="true"></span>
      <p class="pp-cart-progress__eyebrow">Your portrait</p>
      <div role="status" aria-live="polite" aria-atomic="true">
        <h2 id="pp-cart-progress-title">Getting your portrait ready</h2>
        <p class="pp-cart-progress__copy" id="pp-cart-progress-copy">We’re preparing your chosen portrait, size and finish for your cart.</p>
      </div>
      <p class="pp-cart-progress__note">This should only take a moment.</p>`;
    document.body.append(dialog);
    const title = dialog.querySelector('h2');
    const copy = dialog.querySelector('.pp-cart-progress__copy');
    const note = dialog.querySelector('.pp-cart-progress__note');
    let ended = false;
    let hidden = false;
    let fallbackCleanup;
    const close = () => { hidden = true; if (dialog.open) dialog.close(); };
    const showTimer = setTimeout(() => {
      if (ended || hidden || !pageActive || form.isConnected === false) return;
      // Native dialog gives keyboard users the same modal treatment as pointer users.
      if (dialog.isConnected && typeof dialog.showModal === 'function') dialog.showModal();
    }, 250);
    const longerTimer = setTimeout(() => {
      if (!ended) note.textContent = 'Taking a little longer. We’re still working on your cart.';
    }, 6000);
    const progress = {
      form,
      update(stage) {
        if (ended) return;
        const messages = {
          preparing: ['Getting your portrait ready', 'We’re preparing your chosen portrait, size and finish for your cart.'],
          adding: ['Adding to your cart', 'Your portrait and selected options are being added to your cart.'],
          checking: ['Checking your cart', 'We’re confirming your portrait has been added.'],
        };
        const content = messages[stage] || messages.preparing;
        title.textContent = content[0]; copy.textContent = content[1];
      },
      finish() {
        if (ended) return;
        ended = true;
        clearTimeout(showTimer); clearTimeout(longerTimer);
        fallbackCleanup?.();
        close(); dialog.remove();
        if (cartProgress === progress) cartProgress = null;
      },
      followOriginal(sourceVariantId) {
        progress.update('adding');
        const controls = () => [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
        let sawDisabled = controls().some((control) => control.disabled);
        const observer = new MutationObserver((records) => {
          // A very fast request can disable and re-enable before this callback.
          if (records.some((record) => record.attributeName === 'disabled' &&
              (record.oldValue !== null || record.target.disabled))) sawDisabled = true;
          if (sawDisabled && !controls().some((control) => control.disabled)) progress.finish();
          if (form.isConnected === false) progress.finish();
        });
        observer.observe(form, { subtree:true, attributes:true, attributeFilter:['disabled'], attributeOldValue:true });
        const completed = (event) => {
          if (event.detail?.data?.skipAddTracking) return;
          if ([form.id, numericId(sourceVariantId)].filter(Boolean).includes(String(event.detail?.sourceId || ''))) progress.finish();
        };
        const invalid = () => progress.finish();
        document.addEventListener('cart:update', completed, true);
        document.addEventListener('cart:error', completed, true);
        form.addEventListener('invalid', invalid, true);
        // Dismiss only. A timeout must never repeat an uncertain Shopify addition.
        const watchdog = setTimeout(() => progress.finish(), 35_000);
        fallbackCleanup = () => {
          observer.disconnect(); clearTimeout(watchdog);
          document.removeEventListener('cart:update', completed, true);
          document.removeEventListener('cart:error', completed, true);
          form.removeEventListener('invalid', invalid, true);
        };
      },
    };
    dialog.querySelector('button').addEventListener('click', close);
    dialog.addEventListener('cancel', () => { hidden = true; });
    cartProgress = progress;
    return progress;
  };
  const message = (form, text) => {
    statusElement(form).textContent = text;
    if (cartProgress?.form === form) {
      if (text.startsWith('Adding')) cartProgress.update('adding');
      else if (text.startsWith('Checking')) cartProgress.update('checking');
    }
  };
  const showCart = (options) => {
    cartProgress?.finish();
    message(options.form, '');
    const attempt = recalled();
    if (attempt?.phase === 'submitted') remember({ ...attempt, reviewRequestedAt: Date.now() });
    // A lost Shopify response may already have added the item. Never issue a second add blindly.
    location.assign(route('cart'));
  };
  const sections = () => [...new Set([...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const notifyAdded = (options, attempt, cart, renderedSections = {}) => {
    if (attempt.mode !== 'original') {
      confirmCartUse({ requestId: attempt.requestId, productId: attempt.ready.productId, variantId: attempt.ready.variantId });
    }
    // Release dialog focus before the theme opens and focuses its cart drawer.
    cartProgress?.finish();
    remember(null);
    options.added?.();
    message(options.form, 'Your portrait has been added to your cart.');
    const quantity = attempt.items.reduce((sum, item) => sum + Number(item.quantity), 0);
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
      resource: cart || {}, sourceId: options.form.id,
      data: {
        source: 'personalisation-checkout', itemCount: cart?.item_count ?? quantity,
        addedQuantity: Number(attempt.items[0].quantity),
        productId: numericId(attempt.ready.sourceProductId),
        variantId: numericId(attempt.ready.sourceVariantId),
        checkoutProductId: numericId(attempt.ready.productId),
        checkoutVariantId: numericId(attempt.ready.variantId),
        sections: renderedSections,
      },
    } }));
    if (!document.querySelector('cart-drawer-component')) location.assign(route('cart'));
  };
  const prepare = async (options, attempt, background = null, signal = null) => {
    const started = Date.now();
    const checkSelection = () => {
      if (signal?.aborted) throw new Error('Preparation stopped.');
      if (background) {
        if (background.controller.signal.aborted || !pageActive || !options.canWarm?.() ||
            options.isEnabled?.() === false || options.form.isConnected === false ||
            preparationKey(selectionFor(options)) !== background.key) {
          throw new Error('The portrait selection changed.');
        }
      } else assertSelection(options, attempt.fingerprint);
    };
    while (Date.now() - started < 120_000) {
      checkSelection();
      const response = await request(new URL('/apps/pop-prints-backend/checkout-product', location.origin).href, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(attempt.body), signal: background?.controller.signal || signal,
      });
      const result = await response.json().catch(() => ({}));
      if (response.status === 202 && result.status === 'pending') {
        await pause(Math.max(500, Math.min(5000, Number(result.retryAfterMs) || 1500)));
        continue;
      }
      if (!response.ok) {
        if (!background && response.status >= 400 && response.status < 500) remember(null);
        throw new Error(result.message || result.error || 'Your portrait could not be prepared. Please try again.');
      }
      if (result.status !== 'ready' || !/^\d+$/.test(numericId(result.variantId)) ||
          !/^\d+$/.test(numericId(result.productId)) ||
          numericId(result.sourceVariantId) !== numericId(attempt.body.sourceVariantId) ||
          numericId(result.sourceProductId) !== numericId(options.sourceProductId) ||
          (result.portraitShape || 'portrait') !== (attempt.body.portraitShape ?? 'portrait') ||
          (attempt.body.previewThemeId && String(result.previewThemeId || '') !== String(attempt.body.previewThemeId)) ||
          (attempt.body.presentationVersion && result.properties?.['_PP Presentation Version'] !== attempt.body.presentationVersion) ||
          !result.artworkUrl || !result.designId || !result.properties || typeof result.properties !== 'object') {
        throw new Error('Your portrait could not be verified. Please try again.');
      }
      checkSelection();
      options.saveDesign?.(result);
      return result;
    }
    throw new Error('Your portrait is still being prepared. Please try again in a moment.');
  };
  const cancelWarm = () => {
    clearTimeout(warmTimer);
    if (warm?.claimed) return;
    warm?.controller.abort();
    warm = null;
  };
  function scheduleWarm(options) {
    clearTimeout(warmTimer);
    if (busy) return;
    if (!pageActive || options.isEnabled?.() === false || options.form.isConnected === false ||
        !options.canWarm?.() || recalled()?.phase === 'submitted') { cancelWarm(); return; }
    const selection = selectionFor(options);
    if (!/^https:\/\//i.test(String(selection.artworkUrl || '')) || !/^\d+$/.test(numericId(selection.sourceVariantId))) {
      cancelWarm(); return;
    }
    const key = preparationKey(selection);
    if (warm?.key === key && warm.form === options.form && Date.now() - warm.startedAt < 120_000) return;
    cancelWarm();
    warmTimer = setTimeout(() => {
      if (busy || !pageActive || !options.canWarm?.() || options.isEnabled?.() === false ||
          options.form.isConnected === false || preparationKey(selectionFor(options)) !== key ||
          recalled()?.phase === 'submitted') return;
      const previous = recalled();
      const attempt = previous?.phase === 'preparing' && preparationKey(previous.body) === key
        ? previous : newAttempt(options, selectionFor(options));
      const entry = { key, form: options.form, attempt, controller: new AbortController(), startedAt: Date.now(), readyAt: 0 };
      warm = entry;
      // Only prepare the hidden product. The explicit Add action remains the sole cart mutation.
      entry.promise = prepare(options, attempt, entry).then((ready) => {
        entry.readyAt = Date.now();
        return ready;
      }).catch(() => null);
    }, 200);
  }
  window.addEventListener('pagehide', () => { pageActive = false; warm?.controller.abort(); cancelWarm(); cartProgress?.finish(); });
  window.addEventListener('pageshow', () => { pageActive = true; });
  window.addEventListener('pop:portrait-shape-change', cancelWarm);
  async function submitOriginal(options) {
    if (busy) return;
    // This entry point only replaces the original Portrait + digital cart write.
    if (nonPortrait(selectionFor(options))) throw new Error('Please use Add to Cart to prepare this print shape.');
    busy = true;
    options.setBusy(true);
    try {
      await window.PopPrintsPersonalizedCart?.waitForIdle();
      const previous = recalled();
      if (previous?.phase === 'submitted') {
        const cart = await cartSnapshot();
        const state = inspectAttempt(cart, previous);
        if (state === 'complete') { notifyAdded(options, previous, cart); return; }
        if (state !== 'absent' || !previous.reviewRequestedAt) { showCart(options); return; }
        // A new explicit Add after cart review may proceed once absence is checked again.
        remember(null);
      }
      const selection = selectionFor(options);
      if (nonPortrait(selection)) throw new Error('Please use Add to Cart to prepare this print shape.');
      const requestId = crypto.randomUUID();
      const items = options.items().map((item, index) => ({ ...item,
        properties: { ...(item.properties || {}), [ATTEMPT]: requestId, [ROLE]: index ? 'digital' : 'portrait' } }));
      const attempt = { requestId, mode: 'original', fingerprint: fingerprint(selection), phase: 'submitted',
        submittedAt: Date.now(), items, body: selection,
        ready: { sourceProductId: options.sourceProductId, sourceVariantId: items[0].id } };
      remember(attempt);
      let response, data = {}, failure;
      try {
        response = await request(route('cart/add.js'), { method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sections(), sections_url: location.pathname }) });
        data = await response.json().catch(() => ({}));
        if (!response.ok || data.status || data.errors) {
          failure = new Error(data.description || data.message || 'Your portrait could not be added.');
        }
      } catch (error) { failure = error; }
      let cart;
      for (const delay of [0, 1000, 2000]) {
        if (delay) await pause(delay);
        try { cart = await cartSnapshot(); } catch (_) { continue; }
        if (inspectAttempt(cart, attempt) === 'complete') {
          notifyAdded(options, attempt, cart, data.sections || {});
          return;
        }
      }
      if (cart && inspectAttempt(cart, attempt) === 'absent' && response?.status >= 400 && response.status < 500 && response.status !== 408) {
        remember(null);
        throw failure || new Error('Your portrait could not be added. Please try again.');
      }
      showCart(options);
    } catch (error) {
      if (recalled()?.phase === 'submitted') showCart(options);
      else throw error;
    } finally {
      options.setBusy(false);
      busy = false;
    }
  }
  async function submit(options) {
    if (busy) return;
    busy = true;
    options.setBusy(true);
    let progress;
    try { progress = beginCartProgress(options.form); } catch (_) { /* Inline status remains available. */ }
    let preparationTimer;
    const preparationController = new AbortController();
    const submitStarted = Date.now();
    let checkpoint = submitStarted;
    const mark = (stage) => { trace(stage, checkpoint, { totalMs: Date.now() - submitStarted }); checkpoint = Date.now(); };
    mark('start');
    try {
      await window.PopPrintsPersonalizedCart?.waitForIdle();
      mark('wait-for-cart');
      const previous = recalled();
      if (previous?.phase === 'submitted') {
        message(options.form, 'Checking your previous addition...');
        const cart = await cartSnapshot();
        const state = inspectAttempt(cart, previous);
        if (state === 'complete') notifyAdded(options, previous, cart);
        else if (state === 'absent' && previous.reviewRequestedAt) {
          // This is a new explicit Add after opening the cart, with absence checked again.
          remember(null);
          throw new Error('Please try Add to Cart again to prepare your personalized portrait.');
        }
        else showCart(options);
        return;
      }
      message(options.form, 'Preparing your portrait for your cart...');
      if (!await options.ensurePortrait()) throw new Error('Your preview is still saving. Please wait a moment and try again.');
      mark('portrait-save');
      const selection = selectionFor(options);
      const selectedFingerprint = fingerprint(selection);
      const mainItem = options.mainItem();
      const candidate = warm?.key === preparationKey(selection) && Date.now() - warm.startedAt < 120_000 ? warm : null;
      let attempt = previous?.fingerprint === selectedFingerprint ? previous : null;
      if (!attempt) {
        attempt = candidate ? { ...candidate.attempt, fingerprint: selectedFingerprint } : newAttempt(options, selection);
        remember(attempt);
      }
      const adopted = candidate?.attempt.requestId === attempt.requestId ? candidate : null;
      if (adopted) adopted.claimed = true;
      const ready = await Promise.race([
        (async () => {
          const prefetched = adopted ? await adopted.promise : null;
          assertSelection(options, selectedFingerprint);
          // Leave a margin inside the server's five-second freshness window for response transit.
          return prefetched && Date.now() - adopted.readyAt < 4000
            ? prefetched : prepare(options, attempt, null, preparationController.signal);
        })(),
        new Promise((_, reject) => {
          // Keep the overlay active for the existing bounded preparation window.
          preparationTimer = setTimeout(() => reject(new Error('Preparation timed out.')), 120_000);
        }),
      ]);
      clearTimeout(preparationTimer);
      mark('preparation');
      assertSelection(options, selectedFingerprint);
      const properties = { ...(mainItem.properties || {}), ...ready.properties,
        [ATTEMPT]: attempt.requestId, [ROLE]: 'portrait',
        '_Portrait URL': ready.artworkUrl,
        '_PP Return URL': ready.returnUrl || ready.properties['_PP Return URL'] || '',
      };
      const main = { id: numericId(ready.variantId), quantity: Number(mainItem.quantity || 1), properties };
      if (mainItem.selling_plan) throw new Error('This portrait cannot be added with a subscription.');
      const items = [main];
      if (selection.addon) {
        const addon = options.addon(main);
        addon.properties = { ...(addon.properties || {}), [ATTEMPT]: attempt.requestId, [ROLE]: 'digital',
          '_Digital Download Parent Variant': main.id, '_Digital Download Portrait URL': ready.artworkUrl };
        items.push(addon);
      }
      attempt = { ...attempt, ready, items, phase: 'submitted', submittedAt: Date.now() };
      remember(attempt);
      message(options.form, 'Adding your portrait...');
      // Newly published variants can briefly be absent from Shopify's cart service.
      // Retry only its definite rejection, with this entire attempt freshly absent.
      for (let addIndex = 0; addIndex < 3; addIndex += 1) {
        let response;
        let data = {};
        let failure;
        try {
          response = await request(route('cart/add.js'), { method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections: sections(), sections_url: location.pathname }),
          });
          data = await response.json().catch(() => ({}));
          if (!response.ok || data.status || data.errors) {
            failure = new Error(data.description || data.message || 'Your portrait could not be added.');
          }
        } catch (error) { failure = error; }
        mark('cart-add');
        progress?.update('checking');
        if (!failure && Array.isArray(data.items) && items.every((expected) => data.items.some((item) =>
          numericId(item.variant_id || item.id) === numericId(expected.id) && item.properties?.[ATTEMPT] === attempt.requestId))) {
          const cart = await cartSnapshot().catch(() => null);
          mark('cart-refresh');
          notifyAdded(options, attempt, cart, data.sections || {});
          mark('cart-open');
          return;
        }
        // A response can be lost after Shopify commits the add. Match this attempt, not the shared variant.
        let cart = null;
        for (const delay of [0, 1000, 2000]) {
          if (delay) await pause(delay);
          cart = null;
          try { cart = await cartSnapshot(); } catch (_) { continue; }
          if (inspectAttempt(cart, attempt) === 'complete') {
            notifyAdded(options, attempt, cart, data.sections || {});
            return;
          }
          if (inspectAttempt(cart, attempt) === 'partial') {
            showCart(options);
            return;
          }
        }
        const state = cart ? inspectAttempt(cart, attempt) : 'unknown';
        if (state === 'absent' && response?.status === 422 &&
            String(data.description || '').trim() === 'Cannot find variant' && addIndex < 2) {
          await pause(1000);
          cart = await cartSnapshot().catch(() => null);
          const latestState = cart ? inspectAttempt(cart, attempt) : 'unknown';
          if (latestState === 'complete') {
            notifyAdded(options, attempt, cart, data.sections || {});
            return;
          }
          if (latestState !== 'absent' || !pageActive) {
            showCart(options);
            return;
          }
          assertSelection(options, selectedFingerprint);
          message(options.form, 'Adding your portrait...');
          continue;
        }
        if (state === 'absent' && response && response.status >= 400 && response.status < 500) {
          remember(null);
          throw failure || new Error('Your portrait could not be added. Please try again.');
        }
        showCart(options);
        return;
      }
    } catch (error) {
      if (recalled()?.phase === 'submitted') {
        showCart(options);
      } else {
        remember(null);
        message(options.form, 'We could not add your personalized portrait. Please try Add to Cart again.');
      }
    } finally {
      mark('complete');
      clearTimeout(preparationTimer);
      preparationController.abort();
      warm?.controller.abort();
      options.setBusy(false);
      busy = false;
      if (warm) warm.claimed = false;
      cancelWarm();
      progress?.finish();
    }
  }
  window.PopPrintsPersonalizedCheckout = { submit, submitOriginal, scheduleWarm, cancelWarm, confirmCartUse, isBusy: () => busy,
    finishProgress: (form) => { if (cartProgress?.form === form) cartProgress.finish(); },
  };
  void flushCartConfirmations();
})();
